/**
 * Platform → Coding agent: which engine (OMP or OpenCode) new builds and chats run on when the
 * request names none. A pick here beats the API's `STUDIO_DEFAULT_AGENT_ENGINE` and reaches every
 * API pod within seconds; the CLI's `--agent` still beats it.
 */
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminAgentEngine, type AdminDefaultAgentEngine } from '@/api/adminClient';
import { AdminMetaItem } from '@/components/shell/AdminMetaItem';
import { AdminSection } from '@/components/shell/AdminSection';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { LoadError } from '@/components/ui/load-state';

type Choice = AdminAgentEngine | 'server';

export const AGENT_ENGINE_LABELS: Record<AdminAgentEngine, string> = { omp: 'OMP', opencode: 'OpenCode' };

const CHOICES: Array<{ id: Choice; label: string }> = [
  { id: 'omp', label: 'OMP' },
  { id: 'opencode', label: 'OpenCode' },
  { id: 'server', label: 'Server default' },
];

/** Where the engine in use comes from, in plain words. */
export function agentEngineSourceText(view: AdminDefaultAgentEngine): string {
  if (view.source === 'setting') return 'Picked here by an admin.';
  if (view.source === 'env') return 'The server default, set by STUDIO_DEFAULT_AGENT_ENGINE on the API.';
  return 'The built-in server default.';
}

export function confirmText(choice: Choice, view: AdminDefaultAgentEngine): string {
  const engine = choice === 'server' ? view.serverDefault.engine : choice;
  const lead = choice === 'server' ? `Go back to the server default (${AGENT_ENGINE_LABELS[engine]})?` : `Run new builds and chats on ${AGENT_ENGINE_LABELS[engine]}?`;
  return `${lead} It applies to every organization within a few seconds. Builds already running keep their agent.`;
}

export function CodingAgentView({
  view,
  saving,
  onChoose,
}: {
  view: AdminDefaultAgentEngine;
  saving: boolean;
  onChoose: (choice: Choice) => void;
}) {
  const current: Choice = view.setting ?? 'server';
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedFilter label="Default coding agent" options={CHOICES} value={current} onChange={onChoose} />
        {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Saving" /> : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
        <AdminMetaItem label="New builds run on">{AGENT_ENGINE_LABELS[view.engine]}</AdminMetaItem>
        <AdminMetaItem label="Why" className="col-span-2 md:col-span-1">
          <span className="whitespace-normal">{agentEngineSourceText(view)}</span>
        </AdminMetaItem>
        <AdminMetaItem label="Server default">{AGENT_ENGINE_LABELS[view.serverDefault.engine]}</AdminMetaItem>
        <AdminMetaItem label="Last changed">
          {view.updatedAt ? `${new Date(view.updatedAt).toLocaleString()} by ${view.updatedByEmail ?? view.updatedByUserId ?? 'an admin'}` : 'Never'}
        </AdminMetaItem>
      </dl>
    </div>
  );
}

export function CodingAgentSection() {
  const [view, setView] = useState<AdminDefaultAgentEngine | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setError(undefined);
    adminClient
      .getDefaultAgentEngine()
      .then(setView)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load the coding agent'));
  }, []);

  useEffect(load, [load]);

  const choose = async (choice: Choice) => {
    if (!view || saving || choice === (view.setting ?? 'server')) return;
    if (!window.confirm(confirmText(choice, view))) return;
    setSaving(true);
    try {
      const next = await adminClient.putDefaultAgentEngine(choice === 'server' ? null : choice);
      setView(next);
      toast.success(`New builds and chats now run on ${AGENT_ENGINE_LABELS[next.engine]}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save the coding agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminSection
      title="Coding agent"
      description="The agent that writes the code for new builds and answers Chat, for every organization. A change takes effect within a few seconds, with no deploy. Builds already running keep their agent, and the CLI's --agent still picks either."
    >
      {error ? (
        <LoadError message={error} onRetry={load} />
      ) : !view ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the coding agent
        </div>
      ) : (
        <CodingAgentView view={view} saving={saving} onChoose={(choice) => void choose(choice)} />
      )}
    </AdminSection>
  );
}
